import type { FC } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { useWorkflows } from "../context/WorkflowsContext";
import { templateOptions } from "../letters";

const STANDARD = "standard";

/** Draft Letters: which letter types to write, each with its template. */
export const LettersOptions: FC = () => {
  const w = useWorkflows();
  if (w.letterTypes === null)
    return (
      <div className="flex flex-col gap-1.5" aria-label="Loading letter types">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  if (!w.letterTypes.length)
    return (
      <p className="text-sm text-fg-3">
        Casey drafts the letters a {w.settings.visa || "this visa"} filing
        needs, from Firm Instructions.
      </p>
    );
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-sm font-medium text-fg-2">Letters</legend>
      {w.letterTypes.map((t) => {
        const pick = w.letterPicks[t.draftType] ?? {
          on: t.checked ?? true,
          entryId: null
        };
        const options = templateOptions(
          w.library,
          t.draftType,
          w.settings.visa,
          w.settings.defaultTemplates
        );
        const id = `workflow-letter-${t.draftType}`;
        return (
          <div
            key={t.draftType}
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
            data-testid={id}
          >
            <Checkbox
              id={id}
              checked={pick.on}
              onCheckedChange={(on) =>
                w.setLetterPick(t.draftType, { ...pick, on: on === true })
              }
            />
            <label
              htmlFor={id}
              className="min-w-0 flex-1 truncate text-base text-fg"
            >
              {t.label}
            </label>
            <Select
              value={pick.entryId ?? STANDARD}
              disabled={!pick.on}
              onValueChange={(v) =>
                w.setLetterPick(t.draftType, {
                  on: true,
                  entryId: v === STANDARD ? null : v
                })
              }
            >
              <SelectTrigger
                size="sm"
                className="w-44 max-w-full"
                aria-label={`${t.label} Template`}
                data-testid={`${id}-template`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STANDARD}>CaseBlink Standard</SelectItem>
                {options.map((o) => (
                  <SelectItem key={o.entryId} value={o.entryId}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </fieldset>
  );
};
